/**
 * Platform-agnostic image canvas abstraction.
 *
 * Pipeline: raw bytes → IImageCanvas → filters → RawImage.embed() → PdfImage
 *
 * Register the appropriate loader at app startup:
 *   - Browser: setImageCanvasLoader(new BrowserImageCanvasLoader())
 *   - Node.js:  setImageCanvasLoader(new NodeImageCanvasLoader())
 */

/** Holds decoded RGBA pixel data and can produce PNG bytes. */
export interface IImageCanvas {
  readonly width: number;
  readonly height: number;
  /** Raw RGBA pixel data, row-major, 4 bytes per pixel. */
  getRgba(): Uint8Array;
  /** Create a new canvas with the given RGBA data (same dimensions). */
  withRgba(pixels: Uint8Array): IImageCanvas;
  /** Encode to PNG bytes for embedding in a PDF. */
  toPng(): Uint8Array;
}

/** Loads images from various sources and decodes them to IImageCanvas. */
export interface IImageCanvasLoader {
  /** Decode from raw bytes (PNG or JPEG, auto-detected). */
  fromBytes(bytes: Uint8Array): Promise<IImageCanvas>;
  /** Fetch a URL and decode the result. */
  fromUrl(url: string): Promise<IImageCanvas>;
  /** Load from a filesystem path (Node.js only). */
  fromPath(path: string): Promise<IImageCanvas>;
  /** Load from a File object (browser only). */
  fromFile(file: File): Promise<IImageCanvas>;
}

let _loader: IImageCanvasLoader | null = null;

/** Register the platform-specific image canvas loader. Must be called before RawImage.fromBytes/fromUrl/fromPath/fromFile. */
export function setImageCanvasLoader(loader: IImageCanvasLoader): void {
  _loader = loader;
}

/** Get the registered loader. Throws if none has been registered. */
export function getImageCanvasLoader(): IImageCanvasLoader {
  if (!_loader) {
    throw new Error(
      'No IImageCanvasLoader registered. Call setImageCanvasLoader() at app startup.\n' +
        '  Browser: setImageCanvasLoader(new BrowserImageCanvasLoader())\n' +
        '  Node.js: setImageCanvasLoader(new NodeImageCanvasLoader())'
    );
  }
  return _loader;
}
